import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check, X } from "lucide-react";

import { api, type AppNotification, type NotificationSeverity } from "../api";
import { PageHeader } from "../components/PageHeader";

const SEVERITY_STYLE: Record<NotificationSeverity, { label: string; pill: string }> = {
  info: { label: "Info", pill: "bg-sky-500/15 text-sky-300 ring-sky-400/30" },
  warning: { label: "Warning", pill: "bg-amber-500/15 text-amber-300 ring-amber-400/30" },
  critical: { label: "Critical", pill: "bg-red-500/15 text-red-300 ring-red-400/30" },
};

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

function relativeTime(iso: string, now = Date.now()): string {
  const diffS = Math.round((new Date(iso).getTime() - now) / 1000);
  const abs = Math.abs(diffS);
  if (abs < 45) return "just now";
  if (abs < 3600) return RELATIVE.format(Math.round(diffS / 60), "minute");
  if (abs < 86400) return RELATIVE.format(Math.round(diffS / 3600), "hour");
  return RELATIVE.format(Math.round(diffS / 86400), "day");
}

function absoluteTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function NotificationRow({
  item,
  busy,
  onRead,
  onDismiss,
}: {
  item: AppNotification;
  busy: boolean;
  onRead: () => void;
  onDismiss: () => void;
}) {
  const style = SEVERITY_STYLE[item.severity] ?? SEVERITY_STYLE.info;
  const unread = item.read_at === null;
  return (
    <li
      className={
        "flex items-start gap-4 rounded-xl border px-4 py-3 " +
        (unread
          ? "border-brand-purple/30 bg-bf-surface/70"
          : "border-white/5 bg-bf-surface/30")
      }
    >
      <span
        className={
          "mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset " +
          style.pill
        }
      >
        {style.label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <h2 className={"text-sm " + (unread ? "font-semibold text-white" : "font-medium text-bf-text")}>
            {item.title}
          </h2>
          <time
            dateTime={item.created_at}
            title={absoluteTime(item.created_at)}
            className="text-xs text-bf-muted"
          >
            {relativeTime(item.created_at)} · {absoluteTime(item.created_at)}
          </time>
        </div>
        {item.message && <p className="mt-1 text-sm text-bf-muted">{item.message}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {unread && (
          <button
            type="button"
            disabled={busy}
            onClick={onRead}
            className="bf-button bg-white/10 bg-none"
          >
            <Check size={14} className="mr-1.5" />
            Mark read
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={onDismiss}
          className="bf-button bg-white/10 bg-none"
          aria-label={`Dismiss ${item.title}`}
        >
          <X size={14} className="mr-1.5" />
          Dismiss
        </button>
      </div>
    </li>
  );
}

export default function Notifications() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => api.notifications.list({ limit: 100 }),
    refetchInterval: 30_000,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["notifications"] });
  const markRead = useMutation({ mutationFn: api.notifications.markRead, onSuccess: refresh });
  const dismiss = useMutation({ mutationFn: api.notifications.dismiss, onSuccess: refresh });
  const readAll = useMutation({ mutationFn: api.notifications.readAll, onSuccess: refresh });

  const items = list.data ?? [];
  const unread = items.filter((n) => n.read_at === null).length;
  const busy = markRead.isPending || dismiss.isPending || readAll.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        description="Storage, fleet and pipeline conditions that need attention."
        icon={Bell}
        actions={
          unread > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={() => readAll.mutate()}
              className="bf-button bg-white/10 bg-none"
            >
              Mark all read
            </button>
          )
        }
      />

      {list.isError && <p className="text-sm text-red-300">Could not load notifications.</p>}

      {!list.isError && items.length === 0 && !list.isLoading ? (
        <div className="flex h-[40vh] flex-col items-center justify-center gap-3 rounded-xl border border-white/5 bg-bf-surface/30 text-center text-bf-muted">
          <Bell size={32} />
          <p className="text-sm">No notifications.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((n) => (
            <NotificationRow
              key={n.id}
              item={n}
              busy={busy}
              onRead={() => markRead.mutate(n.id)}
              onDismiss={() => dismiss.mutate(n.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
